// src/services/clients/technicalNotesService.ts
// Note tecniche del cliente: la formula colore e tutto ciò che serve per
// ripetere (o non ripetere) quello che si è fatto l'ultima volta.
// Non si sovrascrive: ogni giornata è una versione a sé, così si legge la storia.
import { supabase } from '@/lib/supabase';

export interface TechnicalNote {
    id: number;
    shop_id: number;
    client_id: string;
    content: string;
    note_date: string;   // YYYY-MM-DD
    author_id: string | null;
    author_name: string | null;
    created_at: string;
    updated_at: string;
}

const TABLE = 'CustomerTechnicalNotes';

/** Tutte le note dello shop: servono anche per l'icona in agenda e per la ricerca. */
export const fetchShopTechnicalNotes = async (
    shopId: number | string
): Promise<{ success: boolean; error?: string; data: TechnicalNote[] }> => {
    const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('shop_id', shopId)
        .order('note_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(2000);

    if (error) return { success: false, error: error.message, data: [] };
    return { success: true, data: (data || []) as TechnicalNote[] };
};

export interface SaveTechnicalNoteInput {
    shop_id: number;
    client_id: string;
    content: string;
    note_date: string;
    author_id: string | null;
    author_name: string | null;
}

/**
 * Salvataggio automatico: la versione del giorno viene aggiornata finché si
 * scrive, il giorno dopo ne nasce una nuova (vincolo unico shop/cliente/data/autore).
 */
export const saveTechnicalNote = async (
    input: SaveTechnicalNoteInput
): Promise<{ success: boolean; error?: string; data?: TechnicalNote }> => {
    const { data: existing } = await supabase
        .from(TABLE)
        .select('id')
        .eq('shop_id', input.shop_id)
        .eq('client_id', input.client_id)
        .eq('note_date', input.note_date)
        .eq('author_id', input.author_id)
        .maybeSingle();

    if (existing?.id) {
        const { data, error } = await supabase
            .from(TABLE)
            .update({ content: input.content, updated_at: new Date().toISOString() })
            .eq('id', existing.id)
            .select()
            .single();
        if (error) return { success: false, error: error.message };
        return { success: true, data: data as TechnicalNote };
    }

    const { data, error } = await supabase
        .from(TABLE)
        .insert({
            shop_id: input.shop_id,
            client_id: input.client_id,
            content: input.content,
            note_date: input.note_date,
            author_id: input.author_id,
            author_name: input.author_name,
        })
        .select()
        .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data as TechnicalNote };
};

export const deleteTechnicalNote = async (
    id: number
): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.from(TABLE).delete().eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
};
